import HtmlDiff from "htmldiff-js";
import { useState } from "react";

import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

interface ChatLogProps {
  diffModalContent?: {
    newValue: string;
    oldValue: string;
  };
  message: string;
  shouldShowMore?: boolean;
  timestamp: string;
  tooltipDate?: string;
}

export function ChatLog({
  diffModalContent,
  message,
  shouldShowMore = false,
  timestamp,
  tooltipDate,
}: ChatLogProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClick = () => {
    if (diffModalContent) {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <div
        className={`flex items-start justify-between gap-1 text-sm text-pretty text-base/6 sm:text-sm/6 text-text-primary ${
          shouldShowMore ? "cursor-pointer rounded p-1 -m-1" : ""
        }`}
        onClick={() => {
          if (shouldShowMore) {
            handleClick();
          }
        }}
      >
        <p
          dangerouslySetInnerHTML={{
            __html: `${message} ${shouldShowMore ? "- <b style='color: oklch(48.8% 0.243 264.376);'>Show more<b>" : ""}`,
          }}
        />
        <p
          className="text-text-tertiary text-xs whitespace-nowrap"
          title={tooltipDate}
        >
          {timestamp}
        </p>
      </div>
      {diffModalContent && (
        <DiffModal
          diffModalContent={diffModalContent}
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </>
  );
}

function DiffModal({
  diffModalContent,
  isOpen,
  onOpenChange,
}: {
  diffModalContent: {
    newValue: string;
    oldValue: string;
  };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const diff = HtmlDiff.execute(
    diffModalContent.oldValue,
    diffModalContent.newValue,
  );

  return (
    <Modal
      classNames={{
        modal: "max-h-[655px] max-w-[700px] w-[100%] diff-modal",
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog>
        <DialogHeader>
          <h2 className="text-lg font-semibold text-text-primary">
            Content Changes
          </h2>
          <p className="text-text-tertiary text-sm font-normal">
            The previous content and the updated version are shown below for
            comparison.
          </p>
        </DialogHeader>
        <DialogCloseButton className="cursor-pointer" />

        <DialogBody>
          <div className="grid grid-cols-2 gap-6 h-full mb-8">
            <div className="flex flex-col bg-[#F4F3FF] rounded-lg">
              <div className="bg-red-100 border border-red-200 rounded-lg mx-3 my-3 text-sm font-medium text-red-800 w-[99px] text-center">
                Old Content
              </div>
              <div className="flex-1 p-4 border border-[#EAECF0] border-t-0 rounded-b-lg text-sm whitespace-pre-wrap overflow-auto">
                <p
                  dangerouslySetInnerHTML={{
                    __html: diffModalContent.oldValue,
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col bg-[#F4F3FF] rounded-lg">
              <div className="bg-green-100 border border-green-200 rounded-lg mx-3 my-3 text-sm font-medium text-green-800 w-[99px] text-center">
                New Content
              </div>
              <div className="flex-1 p-4 border border-[#EAECF0] border-t-0 rounded-b-lg text-sm whitespace-pre-wrap overflow-auto">
                <p
                  dangerouslySetInnerHTML={{
                    __html: diff,
                  }}
                />
              </div>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </Modal>
  );
}
